export interface AsyncSwrCache<T> {
  get: () => Promise<T>;
  invalidate: () => void;
}

/**
 * Cache assíncrono stale-while-revalidate para dados de bootstrap.
 *
 * O primeiro acesso aguarda o carregamento. Depois disso o valor conhecido é
 * devolvido imediatamente; ao expirar, uma atualização é disparada em segundo
 * plano sem voltar a bloquear a navegação.
 */
export function createAsyncSwrCache<T>(
  load: () => Promise<T>,
  options: { staleTime: number; now?: () => number },
): AsyncSwrCache<T> {
  const now = options.now ?? Date.now;
  let generation = 0;
  let cached: { value: T; updatedAt: number } | undefined;
  let pending: { generation: number; promise: Promise<T> } | undefined;

  function refresh(): Promise<T> {
    if (pending?.generation === generation) return pending.promise;

    const startedGeneration = generation;
    const promise = load()
      .then((value) => {
        if (generation === startedGeneration) {
          cached = { value, updatedAt: now() };
        }
        return value;
      })
      .finally(() => {
        if (pending?.promise === promise) pending = undefined;
      });
    pending = { generation: startedGeneration, promise };
    return promise;
  }

  return {
    get() {
      if (!cached) return refresh();

      if (now() - cached.updatedAt >= options.staleTime) {
        void refresh().catch(() => undefined);
      }
      return Promise.resolve(cached.value);
    },
    invalidate() {
      generation += 1;
      cached = undefined;
      pending = undefined;
    },
  };
}
